import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import BuildingBanner from '../components/BuildingBanner';
import InteractiveCalendar from '../components/InteractiveCalendar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardCoproprietaire = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  const [assemblees, setAssemblees] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [agRes, incRes, convRes] = await Promise.all([
          axios.get(`${API}/assemblees`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/incidents`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setAssemblees(agRes.data.slice(0, 3));
        setIncidents(incRes.data.slice(0, 4));
        setConversations(convRes.data.slice(0, 2));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token]);

  const getStatusColor = (status) => {
    const colors = {
      'Nouveau': 'border-blue-500',
      'Non traité': 'border-red-500',
      'En cours': 'border-yellow-500',
      'Assigné': 'border-orange-500',
      'Pris en charge': 'border-purple-500',
      'Résolu': 'border-green-500',
      'Fermé': 'border-gray-500'
    };
    return colors[status] || 'border-gray-300';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <>
        {isMobile && (
          <MobileHeader 
            title={`Bonjour ${user?.prenom || ''} 👋`}
            onMenuClick={onMenuClick}
          />
        )}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Mobile Header */}
      {isMobile && (
        <MobileHeader 
          title={`Bonjour ${user?.prenom || ''} 👋`}
          onMenuClick={onMenuClick}
        />
      )}

      <div className={`space-y-4 md:space-y-6 ${isMobile ? 'p-4' : ''}`}>
        {/* Desktop Title */}
        {!isMobile && (
          <h1 className="text-2xl font-bold text-gray-800">Accueil</h1>
        )}

        {/* Building Banner */}
        <BuildingBanner token={token} user={user} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Colonne gauche: Messages + AG + Accès rapide */}
          <div className="space-y-4 md:space-y-6">
            {/* Messages récents */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-base md:text-lg font-semibold text-gray-800">Messages récents</h2>
                <button 
                  onClick={() => navigate('/messagerie')}
                  className="text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium"
                >
                  Voir tout →
                </button>
              </div>
              
              <div className="space-y-3 md:space-y-4">
                {conversations.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">Aucun message</p>
                ) : (
                  conversations.map((conv) => (
                    <div 
                      key={conv.id} 
                      onClick={() => navigate('/messagerie', { state: { selectedConv: conv.id } })}
                      className="pb-3 md:pb-4 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-2 rounded-lg transition-colors"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="w-9 h-9 md:w-10 md:h-10 bg-[#00D3A8] rounded-full flex items-center justify-center text-white font-medium text-sm">
                          {conv.participants?.[0]?.prenom?.[0] || 'M'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-800 text-sm md:text-base truncate">
                              {conv.participants?.map(p => p.prenom).join(', ') || conv.name}
                            </span>
                            <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                              {conv.last_message ? new Date(conv.last_message.created_at).toLocaleDateString('fr-FR') : ''}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-0.5 truncate">
                            {conv.last_message?.content || 'Nouvelle conversation'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Prochaines AG */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-base md:text-lg font-semibold text-gray-800">Prochaines Assemblées</h2>
                <button 
                  onClick={() => navigate('/assemblees')}
                  className="text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium"
                >
                  Voir tout →
                </button>
              </div>
              
              <div className="space-y-3">
                {assemblees.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">Aucune assemblée prévue</p>
                ) : (
                  assemblees.map((ag) => (
                    <div
                      key={ag.id}
                      onClick={() => navigate('/assemblees', { state: { selectedAG: ag.id } })}
                      className="p-3 md:p-4 border border-gray-100 rounded-xl hover:shadow-md hover:border-[#00D3A8] transition-all cursor-pointer"
                    >
                      <div className="text-xs font-bold text-[#00D3A8] uppercase mb-1">
                        {formatDate(ag.date)}
                      </div>
                      <div className="font-medium text-gray-800 text-sm md:text-base">{ag.titre}</div>
                      <div className="text-xs md:text-sm text-gray-500 flex items-center mt-1">
                        <span className="mr-1">📍</span>
                        <span className="truncate">{ag.lieu}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Accès rapide - Déplacé à gauche */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Accès rapide</h2>
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <button
                  onClick={() => navigate('/incidents')}
                  className="p-3 md:p-4 border border-gray-100 rounded-xl hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center"
                  data-testid="quick-access-incidents"
                >
                  <div className="text-xl md:text-2xl mb-1">🚨</div>
                  <div className="text-xs md:text-sm font-medium text-gray-700">Incidents</div>
                </button>
                <button
                  onClick={() => navigate('/messagerie')}
                  className="p-3 md:p-4 border border-gray-100 rounded-xl hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center"
                  data-testid="quick-access-messagerie"
                >
                  <div className="text-xl md:text-2xl mb-1">💬</div>
                  <div className="text-xs md:text-sm font-medium text-gray-700">Messagerie</div>
                </button>
                <button
                  onClick={() => navigate('/assemblees')}
                  className="p-3 md:p-4 border border-gray-100 rounded-xl hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center"
                  data-testid="quick-access-assemblees"
                >
                  <div className="text-xl md:text-2xl mb-1">📋</div>
                  <div className="text-xs md:text-sm font-medium text-gray-700">Assemblées</div>
                </button>
                <button
                  onClick={() => navigate('/annonces')}
                  className="p-3 md:p-4 border border-gray-100 rounded-xl hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center"
                  data-testid="quick-access-annonces"
                >
                  <div className="text-xl md:text-2xl mb-1">📢</div>
                  <div className="text-xs md:text-sm font-medium text-gray-700">Annonces</div>
                </button>
              </div>
            </div>
          </div>

          {/* Colonne droite: Calendrier + Incidents */}
          <div className="space-y-4 md:space-y-6">
            {/* Calendrier interactif - Caché sur très petit mobile */}
            <div className="hidden sm:block">
              <InteractiveCalendar />
            </div>

            {/* Mes incidents */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-base md:text-lg font-semibold text-gray-800">Mes incidents</h2>
                <button 
                  onClick={() => navigate('/incidents-sinistres')}
                  className="text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium"
                >
                  Voir tout →
                </button>
              </div>
              
              <div className="space-y-2 md:space-y-3">
                {incidents.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-sm mb-3">Aucun incident déclaré</p>
                    <button
                      onClick={() => navigate('/incidents-sinistres')}
                      className="bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] hover:opacity-90 text-white text-sm py-2.5 px-5 rounded-full transition-all shadow-sm"
                    >
                      + Déclarer un incident
                    </button>
                  </div>
                ) : (
                  incidents.map((incident) => (
                    <div
                      key={incident.id}
                      onClick={() => navigate('/incidents-sinistres', { state: { selectedIncident: incident.id } })}
                      className={`p-3 border-l-4 ${getStatusColor(incident.status)} bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer rounded-r-lg`}
                    >
                      <div className="font-medium text-gray-800 text-sm md:text-base truncate">{incident.titre}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{incident.status}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardCoproprietaire;
